import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import {
  createCustomer,
  CreateCustomerRequest,
  CreatedResponse,
} from "../../clients/BackOfficeApi";
import { useNavigate } from "react-router-dom";
import { PlusSquareIcon } from "@chakra-ui/icons";
import { isApiError, isBadRequest, ValidationErrorModel } from "../../utils/apiUtils";
import { getDynamicMessageId } from "../I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { ConfirmCloseDirtyDialog } from "./ConfirmCloseDirtyDialog";

export function CreateCustomerModal() {
  const { onOpen, isOpen, onClose } = useDisclosure();
  const [closingDirty, setClosingDirty] = useState(false);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<CreateCustomerRequest>>();
  const [error, setError] = useState<string>();
  const [createCustomerModel, setCreateCustomerModel] =
    useState<CreateCustomerRequest>();
  const navigate = useNavigate();
  const intl = useIntl();

  const isDirty = useMemo(
    () => Boolean(createCustomerModel?.name.trim()),
    [createCustomerModel?.name]
  );

  const isValid = useMemo(
    () => Boolean(createCustomerModel?.name.trim()),
    [createCustomerModel?.name]
  );

  const onCloseDirtyConfirm = useCallback((confirmed: boolean) => {
    setClosingDirty(false);
    if (!confirmed) return;

    setCreateCustomerModel({ name: "" });
    onClose();
  }, [onClose]);

  const onCloseGuarded = useCallback(() => {
    if (!isDirty) {
      onClose();
    }

    setClosingDirty(isDirty);
  }, [isDirty, onClose]);

  const create = useCallback(() => {
    if (!createCustomerModel) return;

    setValidationError(undefined);
    setError(undefined);

    createCustomer({
      body: createCustomerModel,
      throwOnError: true,
    })
      .then((response) => {
        if (response.response.status === 201) {
          const createdResponse = response.data as CreatedResponse;
          navigate(`/customers/${createdResponse.id}`);
          onClose();
        }
      })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }));

          if (isBadRequest(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij het aanmaken van de opdrachtgever: ${e}`);
        }
      });
  }, [createCustomerModel, navigate, onClose, intl]);

  return (
    <>
      <Button onClick={onOpen}>
        <PlusSquareIcon mr={2} />
        Nieuwe opdrachtgever
      </Button>
      <Modal closeOnOverlayClick isOpen={isOpen} onClose={onCloseGuarded}>
        <ModalOverlay />
        <ModalContent>
          <ConfirmCloseDirtyDialog isOpen={closingDirty} onConfirm={onCloseDirtyConfirm} />
          <ModalHeader>Nieuwe opdrachtgever aanmaken</ModalHeader>
          <ModalCloseButton />
          <ModalBody as="form" onSubmit={(e) => { e.preventDefault(); create(); }}>
            {!!error && <Text color="red.500">{error}</Text>}
            <FormControl isInvalid={!!validationError?.Name} isRequired>
              <FormLabel>Naam</FormLabel>
              <Input
                isInvalid={!!validationError?.Name}
                onChange={(e) =>
                  setCreateCustomerModel((c) => ({
                    ...c,
                    name: e.target.value,
                  }))
                }
                placeholder="Voer de naam van de opdrachtgever in"
                value={createCustomerModel?.name ?? ""}
              />
            </FormControl>
            <ErrorList errors={validationError?.Name} />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              isDisabled={!isValid}
              mr={3}
              onClick={create}
            >
              Opslaan
            </Button>
            <Button onClick={onCloseGuarded} variant="ghost">
              Annuleren
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
